/**
 * @fileoverview Pure helpers for `NotesEditor`.
 */

import type { BubbleMenuProps } from "@tiptap/react/menus";
import { ConvexError } from "convex/values";

/** Client-side mirror of the `draftNote` action's prompt limit. */
export const DRAFT_PROMPT_MAX_CHARS = 2000;

/**
 * User-facing text for a failed notes AI call. Only a `ConvexError` carrying
 * a string is shown verbatim — that is how the actions report expected
 * conditions (too long, empty response). Anything else (a thrown `Error`
 * arrives wrapped with Convex request IDs and a stack) gets `fallback`.
 */
export function aiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ConvexError && typeof err.data === "string") {
    return err.data;
  }
  return fallback;
}

/**
 * Escapes `&`, `<`, `>` and `"` so `text` can be embedded as HTML text
 * content (paragraphs and list items). It is not sufficient for attribute
 * values or URLs; the notes editor never puts draft text anywhere else.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * A line that starts a bullet item: optional indentation, then `-`, `•` or
 * `*`, then at least one whitespace character. The trailing whitespace is
 * required so that a hyphenated or starred word (`-ve`, `*note*`) does not
 * count as a bullet.
 */
const BULLET_LINE = /^\s*[-•*]\s+/;

/**
 * Converts the plain text an AI draft returns into the HTML the notes
 * editor stores: blank-line-separated blocks become paragraphs, a block
 * whose every line starts with `- ` (or `•`/`*`) becomes a bullet list, and
 * single line breaks inside a paragraph become `<br>`. Text is escaped, so
 * the model can never inject markup.
 */
export function draftTextToHtml(text: string): string {
  const blocks = text
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);

  return blocks
    .map((block) => {
      const lines = block.split("\n");
      const isList = lines.every((line) => BULLET_LINE.test(line));
      if (isList) {
        const items = lines
          .map(
            (line) =>
              `<li><p>${escapeHtml(line.replace(BULLET_LINE, ""))}</p></li>`,
          )
          .join("");
        return `<ul>${items}</ul>`;
      }
      return `<p>${lines.map((line) => escapeHtml(line.trim())).join("<br>")}</p>`;
    })
    .join("");
}

/** Gap between the selected text and the AI bubble menu, in px. */
export const AI_MENU_OFFSET = 8;

/**
 * Tall enough that the viewport, which Floating UI always intersects with a
 * boundary, is what actually bounds the menu vertically.
 */
const UNBOUNDED_HEIGHT = 1e6;

/**
 * Boundary for `shift`: the editor's horizontal extent, and vertically from
 * the top of the editable area to wherever the viewport ends. Horizontally
 * that keeps the menu inside the editor (a centred menu on a short first
 * word otherwise spills into the sidebar). Vertically it lets `shift` pull
 * the menu back into view when neither above nor below the selection fits,
 * without ever pushing it up into the toolbar, while a menu that only
 * overflows the editor's bottom edge still hangs below it as before.
 */
export function aiBubbleMenuShiftBoundary(content: {
  left: number;
  top: number;
  width: number;
}): { x: number; y: number; width: number; height: number } {
  return {
    x: content.left,
    y: content.top,
    width: content.width,
    height: UNBOUNDED_HEIGHT,
  };
}

/**
 * Floating UI options for the notes editor's AI selection menu.
 *
 * The menu prefers to sit above the selection, but the formatting toolbar
 * sits directly above the editable area — so a selection on the first line
 * or two would place the menu over Bold/Italic/etc. (MOR-1859). Clamping
 * `flip` to the editable content element makes "above" count as
 * overflowing whenever it would leave the content area, and the menu flips
 * below the selection instead. The viewport stays part of the clipping
 * boundary, so a selection near the bottom of the screen still flips back
 * up when the content area is off-screen.
 *
 * `shift` keeps the menu inside the editor horizontally and inside the
 * viewport vertically (see `aiBubbleMenuShiftBoundary`), and `size` caps the
 * menu's width to the editor so it wraps instead of overflowing narrow
 * editors. TipTap runs flip → shift → offset → size, so flip's padding
 * mirrors the offset to reserve the gap when measuring overflow.
 *
 * @param contentBoundary The element wrapping the editable content (not the
 *   toolbar).
 */
export function aiBubbleMenuOptions(
  contentBoundary: Element,
): NonNullable<BubbleMenuProps["options"]> {
  return {
    placement: "top",
    offset: AI_MENU_OFFSET,
    flip: {
      boundary: contentBoundary,
      padding: AI_MENU_OFFSET,
      fallbackPlacements: ["bottom"],
    },
    // Derived per position update so the boundary tracks scroll and resize.
    // TipTap applies `offset` after `shift`, so the bottom padding also
    // reserves the gap offset adds back. No top padding: the boundary's top
    // is the toolbar's bottom edge, and a menu squeezed into a short strip
    // below it must be allowed to sit flush against it.
    shift: () => ({
      boundary: aiBubbleMenuShiftBoundary(
        contentBoundary.getBoundingClientRect(),
      ),
      padding: {
        top: 0,
        bottom: AI_MENU_OFFSET * 2,
        left: AI_MENU_OFFSET,
        right: AI_MENU_OFFSET,
      },
      crossAxis: true,
    }),
    size: {
      boundary: contentBoundary,
      padding: AI_MENU_OFFSET,
      apply: ({ availableWidth, elements }) => {
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.max(0, availableWidth)}px`,
        });
      },
    },
  };
}
